<script>
import store from '@/store/store'
import {
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel
} from '@microsoft/signalr'
import EventBus from '../../event-bus'
import translate from '@/utils/genTable/multilanguageHelper.js'
import httpClient from '@/api/httpClient'
import mapMixin from './mapMixin.js'

export default {
  name: 'BaseMapComponent',
  mixins: [mapMixin],
  data() {
    return {
      wsConnection: null,
      showLoading: true,
      showPoiLogicSyncNotification: false
    }
  },
  props: {
    height: Number
  },
  created() {
    this.$logger('[i] BaseMapComponent created.')
  },
  mounted() {
    this.$logger('[i] BaseMapComponent mounted.')
    this.$logger('[i] Vuex dispatched [getAccessToken]')

    store
      .dispatch('map/getAccessToken', localStorage.getItem('user'))
      .then(() => {
        this.createWsConnection()
        this.setupWsEvents()
        this.handleWSConnectionState()
        this.startWsConnection()
      })

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.wsConnection.stop()
    store.dispatch('map/clearVehicleData')
  },
  methods: {
    init() {},
    createWsConnection() {
      this.wsConnection = new HubConnectionBuilder()
        .withUrl(`${process.env.VUE_APP_API_DOMAIN}/vehiclestream`, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
          accessTokenFactory: () => {
            return store.getters['map/accessToken']
          }
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build()
    },
    setupWsEvents() {
      if (!this.wsConnection) {
        this.$logger(
          `[x] Failed to establish ws connection: [${this.wsConnection}]`,
          'error'
        )

        return
      }

      this.wsConnection.on('InitialData', this.handleInitialData)
      this.wsConnection.on('VehicleData', this.handleVehicleData)
    },
    async startWsConnection() {
      try {
        await this.wsConnection.start()
      } catch (err) {
        setTimeout(this.startWsConnection, 3000)
      }
    },
    handleWSConnectionState() {
      this.wsConnection.onreconnecting((err) => {
        if(this.$route.name === 'GenericMapPage' && this.$route.params.slug === 'realtime') {
          store.dispatch('notification/add', {type: 'warning', message: translate('Connection lost, reconnecting', 'realtimeMap')}, { root: true })
        }
      });
      this.wsConnection.onreconnected(() => {
        if(this.$route.name === 'GenericMapPage' && this.$route.params.slug === 'realtime') {
          store.dispatch('notification/add', {type: 'success', message: translate('Reconnected', 'realtimeMap')}, { root: true })
        }
        this.showLoading = false
      });
      this.wsConnection.onclose((err) => {
        if(this.$route.name === 'GenericMapPage' && this.$route.params.slug === 'realtime') {
          store.dispatch('notification/add', {type: 'error', message: translate('Connection lost, please try again later', 'realtimeMap')}, { root: true })
          store.dispatch('map/clearVehicleData')
        }
      });
    },
    handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return
      if (!this.wsConnection) return
      if (this.wsConnection.state === HubConnectionState.Disconnected) {
        this.startWsConnection()
      }
    },
    async handleInitialData(initialData) {
      this.$logger(`Initial data length ${initialData.length}`)
      store.dispatch('map/saveVehicles', initialData)
      setTimeout(function() {
        EventBus.$emit('SET_ZOOM_LEVEL')
      }, 200)
      this.showLoading = false
      this.handleWSConnectionState()
      await this.checkPoiLogic(initialData.length)
    },
    async handleVehicleData(vehicleData) {
      EventBus.$emit('VEHICLE_STREAM_PACKET_ARRIVED', vehicleData)
      store.dispatch('map/updateVehicle', vehicleData)
    },
    async checkPoiLogic(initialDataLength) {
      if (this.tenant.readRealTimeMapAdditionalColumns.some(x => x.toLowerCase().includes('poi'))) {
        const poisCountResult = await httpClient.get('/pois/GetCount')
        if (initialDataLength >= 100 || poisCountResult.data.totalRecords >= 30) {
          this.showPoiLogicSyncNotification = true
          try {
            const vehicleData = await httpClient.get('/vehicles/HandlePoiLogic')
            store.dispatch('map/saveVehicles', vehicleData.data)
          } finally {
            this.showPoiLogicSyncNotification = false
          }
        }
      }
    }
  }
}
</script>
